import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tenantApi } from '../api/tenantApi';
import { tenantQueryKeys } from './queryKeys';
import { UniversityAdminDTO, InviteUniversityAdminDTO } from '../dtos';
import { TenantInfoDTO } from '../../manage/dtos';
import { UniversityAdminTableData } from '../types';
import { notificationService } from '@shared/components/notifications';
import { useAuthContext } from '@shared/components';
import { userInvitationApi, InviteUserWithRoleDTO } from '../../user-invitation';
import {
  useUsersWithRoles,
  UserRole,
  UserWithRoleDTO,
  userManagementQueryKeys,
} from '../../user-management';
import { trpc } from '@/utils/trpc';

// Query hook for fetching university admins by tenant
// Now uses centralized user management API for consistency
export const useUniversityAdmins = (tenantId: string) => {
  const { data, isLoading, isError, error, refetch, isFetching } = useUsersWithRoles(
    {
      role: UserRole.UNIVERSITY_ADMIN,
      tenantId,
    },
    {
      enabled: !!tenantId,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 3,
    }
  );

  // Transform UserWithRoleDTO[] to match expected UniversityAdminDTO[] format
  const transformedData = data.map((userWithRole: UserWithRoleDTO): UniversityAdminDTO => {
    const universityAdminRole = userWithRole.roles?.find(
      (role) => role.role === UserRole.UNIVERSITY_ADMIN
    );
    return {
      id: userWithRole.id,
      name: userWithRole.name,
      email: userWithRole.email,
      role: UserRole.UNIVERSITY_ADMIN,
      assignedAt: universityAdminRole?.assignedAt || userWithRole.createdAt,
      assignedBy: universityAdminRole?.assignedBy,
      userRoleId: universityAdminRole?.userRoleId || userWithRole.id,
      userId: userWithRole.id,
      tenantId: userWithRole.tenantId,
      status: userWithRole.status,
      expiresAt: universityAdminRole?.expiresAt,
      isExpired: universityAdminRole?.isExpired || false,
    } as UniversityAdminDTO;
  });

  return {
    data: transformedData,
    isLoading,
    isError,
    error,
    refetch,
    isFetching,
  };
};

// Mutation hook for inviting university admin user
export const useInviteUniversityAdmin = (tenantId: string) => {
  const queryClient = useQueryClient();
  const { getCurrentUserId } = useAuthContext();

  return useMutation({
    mutationFn: async (data: InviteUniversityAdminDTO) => {
      // Convert to unified DTO
      const unifiedData: InviteUserWithRoleDTO = {
        email: data.email,
        name: data.name,
        role: 'UNIVERSITY_ADMIN',
        tenantId,
        assignedBy: getCurrentUserId(),
      };

      const response = await userInvitationApi.inviteUserWithRole(unifiedData);
      return response.data;
    },
    onSuccess: (result) => {
      // Invalidate legacy tenant queries
      queryClient.invalidateQueries({ queryKey: tenantQueryKeys.universityAdmins(tenantId) });

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byTenant(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.usersWithRoles({ tenantId }),
      });

      if (result?.success) {
        notificationService.success(result.message || 'University Admin invited successfully');
      }
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to invite University Admin';
      notificationService.error(errorMessage);
    },
  });
};

// Mutation hook for removing university admin role (convenience wrapper)
export const useRemoveUniversityAdmin = (tenantId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      userRoleId,
      userName,
    }: {
      userId: string;
      userRoleId: string;
      userName: string;
    }) => {
      await tenantApi.universityAdminManagement.removeUniversityAdminRole(
        userId,
        tenantId,
        userRoleId
      );
      return { userId, userRoleId, userName };
    },
    onSuccess: ({ userRoleId, userName }) => {
      // Remove from university admins list for this tenant
      queryClient.setQueryData<UniversityAdminDTO[]>(
        tenantQueryKeys.universityAdmins(tenantId),
        (oldData) => {
          if (!oldData) return [];
          return oldData.filter((admin) => admin.userRoleId !== userRoleId);
        }
      );

      // Invalidate user management queries that the table actually uses
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.byTenant(tenantId),
      });
      queryClient.invalidateQueries({
        queryKey: userManagementQueryKeys.usersWithRoles({ tenantId }),
      });

      notificationService.success(`University Admin role removed from ${userName}`);
    },
    onError: (error: any) => {
      const errorMessage = error?.message || 'Failed to remove University Admin role';
      notificationService.error(errorMessage);
    },
  });
};

// Query hook for tenant info using tRPC
export const useTenantInfo = (tenantId: string) => {
  const query = trpc.tenant.getById.useQuery(tenantId, {
    enabled: !!tenantId,
    staleTime: 10 * 60 * 1000, // 10 minutes (tenant info changes less frequently)
  });

  // Transform tRPC response to match TenantInfoDTO format
  const transformedData: TenantInfoDTO | undefined = query.data
    ? {
        id: query.data.tenantId,
        name: query.data.name,
        code: query.data.code,
        description: query.data.description,
      }
    : undefined;

  return {
    ...query,
    data: transformedData,
  };
};

// Computed hook for transforming university admin data for table display
// Simplified since we now get complete data in one call
export const useUniversityAdminsTableData = (tenantId: string) => {
  const { data: universityAdmins, ...queryResult } = useUniversityAdmins(tenantId);

  const tableData: UniversityAdminTableData[] = (universityAdmins || []).map(
    (admin: UniversityAdminDTO) => ({
      id: admin.id,
      name: admin.name || admin.email, // Fallback to email if name not available
      email: admin.email,
      role: admin.role,
      assignedAt: admin.assignedAt,
      userRoleId: admin.userRoleId,
      userId: admin.userId,
      status: admin.status === 'invited' ? 'invited' : 'active',
      expiresAt: admin.expiresAt,
      isExpired: admin.isExpired,
    })
  );

  return {
    data: tableData,
    ...queryResult,
  };
};
